import { useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';

import styles from './AddLeadingBrand.module.scss';
import { IAddLeadingBrandProps } from './IAddLeadingBrandProps';
import { LeadingBrandRequestModel } from '../../../models/leadingBrands/LeadingBrandRequestModel';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';

const AddLeadingBrand = ({
	divisionsOptions,
	countriesOptions,
	setShouldReload
}: IAddLeadingBrandProps): JSX.Element => {
	const [brandId, setBrandId] = useState<number>(1);
	const [divisionId, setDivisionId] = useState<number | undefined>(undefined);
	const [countryId, setCountryId] = useState<number | undefined>(undefined);
	const [productDetailId, setProductDetailId] = useState<number | undefined>(
		undefined
	);
	const [productDetailsOptions, setProductDetailsOptions] = useState<
		DropdownOption[]
	>([]);

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		if (!divisionId) {
			return;
		}
		httpService
			.get<DropdownOption[]>(
				`productDetails/dropdown?divisionId=${divisionId}`,
				undefined
			)
			.then((options) => setProductDetailsOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService, divisionId]);

	const resetDialog = (): void => {
		setBrandId(1);
		setDivisionId(undefined);
		setCountryId(undefined);
		setProductDetailId(undefined);
		setIsVisible(false);
		setIsLoading(false);
	};

	const handleAddLeadingBrand = async (): Promise<void> => {
		if (!brandId || !divisionId || !countryId || !productDetailId) {
			return;
		}

		setIsLoading(true);

		const leadingBrand: LeadingBrandRequestModel = {
			brandId,
			divisionId,
			countryId,
			productDetailId
		};

		try {
			await httpService.post(
				'leadingBrands',
				JSON.stringify(leadingBrand),
				undefined,
				false
			);

			setShouldReload(true);
			resetDialog();
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const addDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleAddLeadingBrand}
				disabled={
					!brandId || !divisionId || !countryId || !productDetailId
				}
			>
				Add
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles.dialogFooter}>
			<p>Creating Leading Brand...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				className={`${styles.addButton} btn primary`}
				onClick={() => setIsVisible(true)}
			>
				Add Leading Brand
			</button>

			<Dialog
				className={styles.dialog}
				header='Add Leading Brand'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles.dialogContent}>
					<label htmlFor='brandId'>Brand</label>
					<InputText id='brandId' value='Atlas Copco' readOnly />
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='divisionId'>Division</label>
					<Dropdown
						id='divisionId'
						required
						placeholder='Select Division'
						value={divisionId}
						options={divisionsOptions}
						onChange={(e: DropdownChangeParams) => {
							setDivisionId(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='countryId'>Country</label>
					<Dropdown
						id='countryId'
						required
						filter
						placeholder='Select Country'
						value={countryId}
						options={countriesOptions}
						onChange={(e: DropdownChangeParams) => {
							setCountryId(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='productDetailId'>Product Category</label>
					<Dropdown
						id='productDetailId'
						required
						filter
						placeholder='Select Product Category'
						disabled={!divisionId}
						value={productDetailId}
						options={productDetailsOptions}
						onChange={(e: DropdownChangeParams) => {
							setProductDetailId(e.target.value);
						}}
					/>
				</div>
			</Dialog>
		</>
	);
};

export default AddLeadingBrand;
